import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { podeNaConta } from './escopos';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, a rota decide pelo `meta.escopos`, e não pelo
// `meta.permissions`: o escopo, ou a lista em que basta um, que o servidor pede para a tela (`Acesso::Mapa`). A lista
// vazia é a rota de todo membro da conta (a Home, o perfil, as notificações), como as ações `PUBLICAS` e
// `DA_PROPRIA_PESSOA` do mapa. Sem `meta.escopos`, a rota fica fechada, como a sem `meta.permissions` hoje.
export const rotaAbertaPelosEscopos = (escopos, pode) => {
  if (!escopos) return false;
  const exigidos = [escopos].flat();
  return exigidos.length === 0 || pode(exigidos);
};

// A primeira tela que a pessoa alcança pelos escopos, como o `defaultRedirectPage` pelo papel. Sem nenhuma delas, a
// Home, que é de todo membro: o painel, para quem não o alcança, a devolveria ao guard, em círculo.
const TELAS_POR_ESCOPO = [
  { escopos: ['conversations:participating'], path: 'dashboard' },
  { escopos: ['contacts:read'], path: 'contacts' },
  { escopos: ['reports:read'], path: 'reports/atendimento' },
];

export const primeiraTelaPelosEscopos = (to, pode) => {
  const tela = TELAS_POR_ESCOPO.find(({ escopos }) => pode(escopos));
  return `accounts/${to.params.accountId}/${tela ? tela.path : 'home'}`;
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/atendimento' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  // ACESSO-01 (L8b): a chave ligada, os escopos da pessoa na conta da rota; desligada, o papel, como hoje.
  if (user?.acesso_granular === true) {
    const { meta: { escopos } = {} } = to;
    const pode = podeNaConta(user, to.params.accountId);
    return rotaAbertaPelosEscopos(escopos, pode)
      ? null
      : primeiraTelaPelosEscopos(to, pode);
  }

  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
